import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBlog } from "@/lib/api";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import Seo, { SITE_URL } from "@/components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getBlog(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container-x py-32 text-center" data-testid="blog-not-found">
      <h1 className="text-4xl font-display text-[color:var(--dh-navy)]">Post not found</h1>
      <Link to="/blog" className="btn-primary mt-6 inline-flex">Back to Blog</Link>
    </div>
  );

  if (!post) return <div className="container-x py-32 text-center text-[color:var(--dh-slate)]">Loading…</div>;

  return (
    <div data-testid={`blog-post-${slug}`}>
      <Seo
        title={post.title}
        description={post.excerpt}
        image={post.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          articleSection: post.category,
          datePublished: post.created_at,
          url: `${SITE_URL}/blog/${post.slug}`,
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          author: { "@type": "Organization", name: "Digi Hub Solution" },
          publisher: { "@type": "Organization", name: "Digi Hub Solution", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo512.png` } },
        }}
      />
      <section className="hero-gradient text-white">
        <div className="container-x py-16 md:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <h1 className="mt-6 text-3xl md:text-5xl max-w-3xl">{post.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-white/70">
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> {post.date}</span>
            <span className="inline-flex items-center gap-1"><Tag className="w-4 h-4" /> {post.category}</span>
          </div>
        </div>
      </section>

      <article className="container-x py-16 max-w-3xl">
        <img src={post.image} alt={post.title} className="rounded-2xl w-full h-[360px] object-cover border border-[color:var(--dh-line)]" />
        <div className="mt-8 space-y-4 text-[color:var(--dh-slate)] leading-relaxed text-lg">
          {post.content.split("\n\n").map((para, i) => <p key={`${slug}-p-${i}`}>{para}</p>)}
        </div>
      </article>
    </div>
  );
}
